import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  IconButton,
  Button,
  Collapse,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  MoreVert,
  UnfoldMore,
  UnfoldLess,
  KeyboardArrowRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { TestTarget as OperationLogTestTarget } from "../services/operationLogService";
import { TestTarget as AppTestTarget } from "../types/TestTarget";
import { getFirstServiceBaseUrlBlockOrEmpty, sanitizeBaseUrlBlockForPersistence } from "../utils/serviceUtils";

// ───────────────────────── 型定義 ─────────────────────────

interface PageResult {
  group_index: number;
  path: string;
  test_targets: OperationLogTestTarget[];
  usecases: any[];
  is_new_view?: boolean;
  view_id?: string;
}

interface Target_change_summary {
  has_changes: boolean;
  change_labels: string[];
  field_changes: {
    label: string;
    old_value: string | number | null | undefined;
    new_value: string | number | null | undefined;
  }[];
  params_changed: boolean;
  usecases_changed: boolean;
}

export interface SpecRegistrationHierarchicalViewProps {
  selectedPage: PageResult | null;
  selectedPageTargets: OperationLogTestTarget[];
  selectedTargets: OperationLogTestTarget[];
  setSelectedTargets: React.Dispatch<
    React.SetStateAction<OperationLogTestTarget[]>
  >;
  findExistingTarget: (targetId: string) => AppTestTarget | null;
  get_target_change_summary: (
    target: OperationLogTestTarget,
    existing: AppTestTarget | null
  ) => Target_change_summary;
  deleteCandidateIds?: Set<string>;
  setDeleteCandidateIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** 削除対象ページ（is_deletion_page=true）かどうか。true のとき選択ツールバーを非表示にする */
  isDeletionPage?: boolean;
}

// ───────────────────── ツリー構築ユーティリティ ──────────────────────

export interface TreeNode {
  target: OperationLogTestTarget;
  children: TreeNode[];
  depth: number;
}

export function buildTree(targets: OperationLogTestTarget[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  for (const t of targets) {
    const id = t.test_target_id ?? "";
    byId.set(id, { target: t, children: [], depth: 0 });
  }

  for (const t of targets) {
    const id = t.test_target_id ?? "";
    const parentId = t.parent_test_target_id;
    const node = byId.get(id)!;

    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const assignDepth = (nodes: TreeNode[], depth: number) => {
    for (const n of nodes) {
      n.depth = depth;
      assignDepth(n.children, depth + 1);
    }
  };
  assignDepth(roots, 0);

  return roots;
}

export function getDescendantIds(node: TreeNode): string[] {
  const ids: string[] = [];
  const traverse = (n: TreeNode) => {
    const id = n.target.test_target_id;
    if (id) ids.push(id);
    n.children.forEach(traverse);
  };
  node.children.forEach(traverse);
  return ids;
}

export function getAllIdsInSubtree(node: TreeNode): string[] {
  const ids: string[] = [];
  const traverse = (n: TreeNode) => {
    const id = n.target.test_target_id;
    if (id) ids.push(id);
    n.children.forEach(traverse);
  };
  traverse(node);
  return ids;
}

export function getAllNodeIds(nodes: TreeNode[]): string[] {
  const ids: string[] = [];
  const traverse = (n: TreeNode) => {
    const id = n.target.test_target_id;
    if (id) ids.push(id);
    n.children.forEach(traverse);
  };
  nodes.forEach(traverse);
  return ids;
}

// ──────────────────────── 色・ラベルユーティリティ ────────────────────────

function getTypeChipColor(
  type?: string
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" {
  switch (type?.toLowerCase()) {
    case "service":
      return "primary";
    case "view":
      return "secondary";
    case "component":
      return "success";
    case "section":
      return "warning";
    case "overlay":
      return "error";
    case "item":
      return "info";
    default:
      return "default";
  }
}

function getOperationChipColor(
  op?: string
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" {
  switch (op) {
    case "CREATE":
    case "ADD":
      return "success";
    case "UPDATE":
      return "warning";
    case "DELETE":
      return "error";
    case "NO_CHANGE":
      return "default";
    default:
      return "default";
  }
}

function getDisplayOperation(
  target: OperationLogTestTarget | undefined,
  summary: Target_change_summary | null | undefined,
): string | undefined {
  if (!target?.operation) return undefined;
  if (target.operation === "UPDATE" && summary && !summary.has_changes) {
    return "NO_CHANGE";
  }
  return target.operation;
}

function getOperationChipLabel(operation?: string): string {
  return operation === "NO_CHANGE" ? "変更なし" : operation ?? "";
}

// ──────────────────────── Service ヘッダー ────────────────────────

interface ServiceHeaderProps {
  serviceTarget: OperationLogTestTarget | undefined;
  pageTargets: OperationLogTestTarget[];
  findExistingTarget: (id: string) => AppTestTarget | null;
  get_target_change_summary: SpecRegistrationHierarchicalViewProps["get_target_change_summary"];
  selectedMap: Map<string, OperationLogTestTarget>;
  setSelectedTargets: React.Dispatch<React.SetStateAction<OperationLogTestTarget[]>>;
  deleteCandidateIds?: Set<string>;
  setDeleteCandidateIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
  serviceSubtreeIds?: string[];
  isDeletionPage?: boolean;
}

function ServiceHeader({
  serviceTarget,
  pageTargets,
  findExistingTarget,
  get_target_change_summary,
  selectedMap,
  setSelectedTargets,
  deleteCandidateIds,
  setDeleteCandidateIds,
  serviceSubtreeIds,
  isDeletionPage = false,
}: ServiceHeaderProps) {
  const [expanded, setExpanded] = useState(false);

  const existing = serviceTarget
    ? findExistingTarget(serviceTarget.test_target_id ?? "")
    : null;
  const summary =
    serviceTarget && get_target_change_summary
      ? get_target_change_summary(serviceTarget, existing)
      : null;

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of pageTargets) {
      const k = (t.type ?? "unknown").toLowerCase();
      counts[k] = (counts[k] ?? 0) + 1;
    }
    return counts;
  }, [pageTargets]);

  const displayName = serviceTarget?.name ?? "(Service)";
  const operation = getDisplayOperation(serviceTarget, summary);

  // Service サブツリー全体の一括チェックボックス状態。
  // 削除ページは deleteCandidateIds、それ以外は各ターゲットの selected を参照する。
  const subtreeIds = serviceSubtreeIds ?? [];
  const checkedCount = subtreeIds.filter((sid) =>
    isDeletionPage ? deleteCandidateIds?.has(sid) : selectedMap.get(sid)?.selected,
  ).length;
  const allChecked = subtreeIds.length > 0 && checkedCount === subtreeIds.length;
  const someChecked = checkedCount > 0 && checkedCount < subtreeIds.length;

  const handleToggleSubtree = (checked: boolean) => {
    if (isDeletionPage) {
      if (!setDeleteCandidateIds) return;
      setDeleteCandidateIds((prev) => {
        const next = new Set(prev);
        subtreeIds.forEach((sid) => (checked ? next.add(sid) : next.delete(sid)));
        return next;
      });
      return;
    }
    const idSet = new Set(subtreeIds);
    setSelectedTargets((prev) =>
      prev.map((t) =>
        idSet.has(t.test_target_id ?? "") ? { ...t, selected: checked } : t,
      ),
    );
  };

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "grey.100",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {subtreeIds.length > 0 && (
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={someChecked}
            onChange={(e) => handleToggleSubtree(e.target.checked)}
            inputProps={{ "aria-label": `${isDeletionPage ? "削除" : "選択"}: ${displayName}` }}
            sx={{ p: 0.25, ...(isDeletionPage && { color: "error.main" }) }}
          />
        )}
        {operation && (
          <Chip
            label={getOperationChipLabel(operation)}
            size="small"
            color={getOperationChipColor(operation)}
          />
        )}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
          {displayName}
        </Typography>
        <Chip label="Service" size="small" variant="outlined" color={getTypeChipColor("service")} />
        <Button
          size="small"
          endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setExpanded((v) => !v)}
          sx={{ fontSize: "0.75rem" }}
        >
          ⓘ 詳細を見る
        </Button>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1, pl: 1 }}>
          {(() => {
            // string レガシー (base_url / baseUrl 両形式) → 配列形式の順でフォールバック。
            // 配列形式は最初のブロックの最初の URL を取り出し (default 環境固定でなく任意キーを許容)、
            // sanitizeBaseUrlBlockForPersistence で内部メタキー (__meta_row_order__ 等) を除外。
            const params = serviceTarget?.params as
              | { base_url?: unknown; baseUrl?: unknown }
              | undefined;
            const stringRaw =
              typeof params?.base_url === "string"
                ? params.base_url
                : typeof params?.baseUrl === "string"
                  ? params.baseUrl
                  : "";
            let display = stringRaw;
            if (!display) {
              const block = sanitizeBaseUrlBlockForPersistence(
                getFirstServiceBaseUrlBlockOrEmpty(params),
              );
              // 先頭キーの値が空文字でも、他環境キーに非空 URL があればそれを採用
              display = Object.values(block).find((v) => typeof v === "string" && v.length > 0) ?? "";
            }
            if (!display) return null;
            return (
              <Typography variant="caption" display="block" color="text.secondary">
                {`URL: ${display}`}
              </Typography>
            );
          })()}
          {serviceTarget?.description && (
            <Typography variant="caption" display="block" color="text.secondary">
              {serviceTarget.description}
            </Typography>
          )}
          <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
            {Object.entries(typeCounts)
              .filter(([k]) => k !== "service")
              .map(([k, cnt]) => (
                <Chip
                  key={k}
                  label={`${k}: ${cnt}`}
                  size="small"
                  variant="outlined"
                  color={getTypeChipColor(k)}
                  sx={{ fontSize: "0.7rem", height: "20px" }}
                />
              ))}
          </Box>
          {summary && summary.field_changes.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {summary.field_changes.map((fc) => (
                <Box key={fc.label} sx={{ mb: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold">
                    {fc.label}:
                  </Typography>
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ ml: 1 }}
                  >
                    {String(fc.old_value ?? "(空)")}
                  </Typography>
                  <Typography variant="caption" sx={{ mx: 0.5 }}>
                    →
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {String(fc.new_value ?? "(空)")}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ──────────────────────── 差分パネル ────────────────────────

interface DiffPanelProps {
  target: OperationLogTestTarget;
  existing: AppTestTarget | null;
  summary: Target_change_summary;
}

function DiffPanel({ target, existing, summary }: DiffPanelProps) {
  type RowStatus = "new" | "changed" | "unchanged";
  const basicRows: { label: string; status: RowStatus; oldVal?: string; newVal: string }[] = [];
  type ParamRow = { key: string; status: RowStatus; oldVal?: string; newVal: string };

  const fmt = (v: unknown): string => {
    if (v === null || v === undefined || v === "") return "(空)";
    if (Array.isArray(v) && v.every((entry) => entry == null || typeof entry !== "object")) {
      return v.map((entry) => String(entry ?? "")).join("\n");
    }
    if (typeof v === "object") return JSON.stringify(v, null, 2);
    return String(v);
  };
  const mono = (s: string) => (s.includes("\n") ? "monospace" : undefined);
  const isEmptyValue = (v: unknown): boolean => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string" && v.trim() === "") return true;
    return false;
  };
  const sortValue = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(sortValue);
    if (v && typeof v === "object") {
      return Object.keys(v as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = sortValue((v as Record<string, unknown>)[key]);
          return acc;
        }, {});
    }
    return v;
  };
  const equivalent = (a: unknown, b: unknown): boolean => {
    if (isEmptyValue(a) && isEmptyValue(b)) return true;
    return JSON.stringify(sortValue(a)) === JSON.stringify(sortValue(b));
  };
  const hiddenParamKeys = new Set([
    "status",
    "maskTags",
    "maskType",
    "isManual",
    "is_manual",
    "failCount",
    "fail_count",
    "isDownload",
    "is_download",
    "customTypeFlg",
    "forceAnalyze",
    "force_analyze",
    "automationStats",
  ]);
  const cleanParams = (params: unknown): Record<string, unknown> => {
    if (!params || typeof params !== "object") return {};
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
      if (hiddenParamKeys.has(key)) continue;
      if (isEmptyValue(value)) continue;
      out[key] = value;
    }
    if (Array.isArray(out.itemCategories)) {
      const entries = out.itemCategories.filter(
        (entry): entry is Record<string, unknown> =>
          entry != null && typeof entry === "object",
      );
      if (entries.length > 0) {
        out.itemCategoryPair = entries.map(
          (entry) =>
            `${String(entry.itemCategory ?? "")} / ${String(entry.itemSubCategory ?? "—")}`,
        );
        delete out.itemCategory;
        delete out.itemSubCategory;
        delete out.itemCategories;
      }
    }
    if (!out.itemCategoryPair && out.itemCategory) {
      out.itemCategoryPair = `${String(out.itemCategory)} / ${String(out.itemSubCategory ?? "—")}`;
      delete out.itemCategory;
      delete out.itemSubCategory;
    }
    delete out.objectTypes;
    return out;
  };
  const displayParamKey = (key: string): string => {
    if (key === "itemCategoryPair") return "項目カテゴリ / サブカテゴリ";
    if (key === "itemCategory") return "項目カテゴリ";
    if (key === "itemSubCategory") return "サブカテゴリ";
    return key;
  };

  const fcKeys = new Set(summary.field_changes.map((f) => f.label));

  // 基本フィールド (名前/タイプ/説明) は既存との差分付きで表示
  if (!existing) {
    basicRows.push({ label: "名前", status: "new", newVal: fmt(target.name) });
    basicRows.push({ label: "タイプ", status: "new", newVal: fmt(target.type) });
    basicRows.push({ label: "説明", status: "new", newVal: fmt(target.description) });
  } else {
    basicRows.push({ label: "名前", status: fcKeys.has("名前") ? "changed" : "unchanged", oldVal: fmt(existing.name), newVal: fmt(target.name) });
    basicRows.push({ label: "タイプ", status: fcKeys.has("タイプ") ? "changed" : "unchanged", oldVal: fmt(existing.type), newVal: fmt(target.type) });
    basicRows.push({ label: "説明", status: fcKeys.has("説明") ? "changed" : "unchanged", oldVal: fmt(existing.description), newVal: fmt(target.description) });
  }

  const newParams = cleanParams(target.params);
  const oldParams = cleanParams(existing?.params);
  const paramRows: ParamRow[] = Object.keys(newParams)
    .sort()
    .map((key) => ({
      key,
      status: !existing || !(key in oldParams)
        ? "new"
        : equivalent(newParams[key], oldParams[key])
          ? "unchanged"
          : "changed",
      oldVal: key in oldParams ? fmt(oldParams[key]) : undefined,
      newVal: fmt(newParams[key]),
    }));

  return (
    <Box
      sx={{
        pl: 2,
        pr: 1,
        py: 1,
        bgcolor: "grey.50",
        borderLeft: 2,
        borderColor: "primary.light",
        fontSize: "0.75rem",
      }}
    >
      {basicRows.map((row) => (
        <Box key={row.label} display="flex" alignItems="flex-start" gap={1} mb={0.5}>
          <Chip
            label={row.status === "new" ? "新規" : row.status === "changed" ? "変更" : "変更なし"}
            size="small"
            color={row.status === "new" ? "success" : row.status === "changed" ? "warning" : "default"}
            sx={{ fontSize: "0.65rem", height: "18px", minWidth: "48px" }}
          />
          <Typography variant="caption" fontWeight="bold" sx={{ minWidth: "72px", flexShrink: 0 }}>
            {row.label}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
            {row.status === "changed" && row.oldVal !== undefined && (
              <Typography
                variant="caption"
                sx={{
                  color: "error.main",
                  textDecoration: "line-through",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: mono(row.oldVal),
                }}
              >
                {row.oldVal}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                color: row.status === "new" ? "success.main" : row.status === "changed" ? "text.primary" : "text.secondary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: mono(row.newVal),
              }}
            >
              {row.newVal}
            </Typography>
          </Box>
        </Box>
      ))}

      {paramRows.length > 0 && (
        <>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: "bold", color: "text.secondary", mt: 1, mb: 0.5 }}
          >
            params
          </Typography>
          {paramRows.map((row) => (
            <Box key={row.key} display="flex" alignItems="flex-start" gap={1} mb={0.75}>
              <Chip
                label={row.status === "new" ? "追加" : row.status === "changed" ? "変更" : "変更なし"}
                size="small"
                color={row.status === "new" ? "success" : row.status === "changed" ? "warning" : "default"}
                sx={{ fontSize: "0.65rem", height: "18px", minWidth: "48px" }}
              />
              <Typography variant="caption" fontWeight="bold" sx={{ minWidth: "120px", flexShrink: 0 }}>
                {displayParamKey(row.key)}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
                {row.status === "changed" && row.oldVal !== undefined && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "error.main",
                      textDecoration: "line-through",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: mono(row.oldVal),
                    }}
                  >
                    {row.oldVal}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: row.status === "new" ? "success.main" : row.status === "changed" ? "text.primary" : "text.secondary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: mono(row.newVal),
                  }}
                >
                  {row.newVal}
                </Typography>
              </Box>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}

// ──────────────────────── ケバブメニュー（サブツリー操作）────────────────────────

interface SubtreeMenuProps {
  node: TreeNode;
  expandedNodes: Set<string>;
  setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSelectedTargets: React.Dispatch<
    React.SetStateAction<OperationLogTestTarget[]>
  >;
  findExistingTarget: (id: string) => AppTestTarget | null;
  get_target_change_summary: SpecRegistrationHierarchicalViewProps["get_target_change_summary"];
  deleteCandidateIds?: Set<string>;
  setDeleteCandidateIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
  isDeletionPage?: boolean;
}

function SubtreeMenu({
  node,
  expandedNodes,
  setExpandedNodes,
  setSelectedTargets,
  findExistingTarget,
  get_target_change_summary,
  deleteCandidateIds,
  setDeleteCandidateIds,
  isDeletionPage = false,
}: SubtreeMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const subtreeIds = useMemo(() => getAllIdsInSubtree(node), [node]);
  const descendantIds = useMemo(() => getDescendantIds(node), [node]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const expandSubtree = () => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      subtreeIds.forEach((id) => next.add(id));
      return next;
    });
    handleClose();
  };

  const collapseSubtree = () => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      subtreeIds.forEach((id) => next.delete(id));
      return next;
    });
    handleClose();
  };

  const selectSubtree = (checked: boolean) => {
    const ids = new Set(descendantIds);
    ids.add(node.target.test_target_id ?? "");
    setSelectedTargets((prev) =>
      prev.map((t) =>
        ids.has(t.test_target_id ?? "") ? { ...t, selected: checked } : t
      )
    );
    handleClose();
  };

  const selectByOperation = (op: "CREATE" | "UPDATE") => {
    const ids = new Set(subtreeIds);
    setSelectedTargets((prev) =>
      prev.map((t) => {
        if (!ids.has(t.test_target_id ?? "")) return t;
        const existing = findExistingTarget(t.test_target_id ?? "");
        const summary = get_target_change_summary(t, existing);
        const displayOperation = getDisplayOperation(t, summary);
        return { ...t, selected: displayOperation === op };
      })
    );
    handleClose();
  };

  const selectByType = (types: string[]) => {
    const ids = new Set(subtreeIds);
    setSelectedTargets((prev) =>
      prev.map((t) => {
        if (!ids.has(t.test_target_id ?? "")) return t;
        return {
          ...t,
          selected: types.includes((t.type ?? "").toLowerCase()),
        };
      })
    );
    handleClose();
  };

  const selectWithChanges = () => {
    const ids = new Set(subtreeIds);
    setSelectedTargets((prev) =>
      prev.map((t) => {
        if (!ids.has(t.test_target_id ?? "")) return t;
        const existing = findExistingTarget(t.test_target_id ?? "");
        const summary = get_target_change_summary(t, existing);
        return { ...t, selected: summary.has_changes };
      })
    );
    handleClose();
  };

  const invertSelection = () => {
    const ids = new Set(subtreeIds);
    setSelectedTargets((prev) =>
      prev.map((t) => {
        if (!ids.has(t.test_target_id ?? "")) return t;
        return { ...t, selected: !t.selected };
      })
    );
    handleClose();
  };

  const setSubtreeDeleteCandidates = (add: boolean) => {
    if (!setDeleteCandidateIds) return;
    const ids = subtreeIds;
    setDeleteCandidateIds((prev) => {
      const next = new Set(prev);
      if (add) {
        ids.forEach((id) => next.add(id));
      } else {
        ids.forEach((id) => next.delete(id));
      }
      return next;
    });
    handleClose();
  };

  const nodeName = node.target.name ?? "(無名)";
  const nodeType = node.target.type ?? "";
  const subtreeSize = subtreeIds.length;

  return (
    <>
      <Tooltip title="サブツリー操作">
        <IconButton size="small" onClick={handleOpen} sx={{ opacity: 0.6 }} aria-label="サブツリー操作">
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem disabled dense>
          <Typography variant="caption" color="text.secondary">
            サブツリー: {nodeName} ({nodeType}, {subtreeSize} 件)
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={expandSubtree} dense>
          <UnfoldMore fontSize="small" sx={{ mr: 1 }} />
          このサブツリーを全展開
        </MenuItem>
        <MenuItem onClick={collapseSubtree} dense>
          <UnfoldLess fontSize="small" sx={{ mr: 1 }} />
          このサブツリーを全折りたたみ
        </MenuItem>
        {!isDeletionPage && (
          <>
            <Divider />
            <MenuItem onClick={() => selectSubtree(true)} dense>
              ☑ このサブツリーを全選択
            </MenuItem>
            <MenuItem onClick={() => selectSubtree(false)} dense>
              ☐ このサブツリーを全解除
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => selectByType(["item"])} dense>
              📑 Item のみ選択
            </MenuItem>
            <MenuItem onClick={() => selectByType(["section", "overlay"])} dense>
              🗂 Section / Overlay のみ選択
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => selectByOperation("CREATE")} dense>
              ✓ 新規 (CREATE) のみ選択
            </MenuItem>
            <MenuItem onClick={() => selectByOperation("UPDATE")} dense>
              ✎ 更新 (UPDATE) のみ選択
            </MenuItem>
            <MenuItem onClick={selectWithChanges} dense>
              ⚡ 変更ありのみ選択
            </MenuItem>
            <MenuItem onClick={invertSelection} dense>
              ⇄ このサブツリーの選択を反転
            </MenuItem>
          </>
        )}
        {setDeleteCandidateIds && (
          <>
            <Divider />
            <MenuItem onClick={() => setSubtreeDeleteCandidates(true)} dense>
              🗑 サブツリーを削除対象に
            </MenuItem>
            <MenuItem onClick={() => setSubtreeDeleteCandidates(false)} dense>
              ↩ サブツリーの削除対象を解除
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}

// ──────────────────────── ツリーノード ────────────────────────

interface TreeNodeRowProps {
  node: TreeNode;
  expandedNodes: Set<string>;
  detailExpandedIds: Set<string>;
  selectedMap: Map<string, OperationLogTestTarget>;
  setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  setDetailExpandedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSelectedTargets: React.Dispatch<
    React.SetStateAction<OperationLogTestTarget[]>
  >;
  findExistingTarget: (id: string) => AppTestTarget | null;
  get_target_change_summary: SpecRegistrationHierarchicalViewProps["get_target_change_summary"];
  deleteCandidateIds?: Set<string>;
  setDeleteCandidateIds?: React.Dispatch<React.SetStateAction<Set<string>>>;
  isDeletionPage?: boolean;
}

function TreeNodeRow({
  node,
  expandedNodes,
  detailExpandedIds,
  selectedMap,
  setExpandedNodes,
  setDetailExpandedIds,
  setSelectedTargets,
  findExistingTarget,
  get_target_change_summary,
  deleteCandidateIds,
  setDeleteCandidateIds,
  isDeletionPage = false,
}: TreeNodeRowProps) {
  const id = node.target.test_target_id ?? "";
  const isExpanded = expandedNodes.has(id);
  const isDetailExpanded = detailExpandedIds.has(id);
  const hasChildren = node.children.length > 0;

  const thisSel = selectedMap.get(id)?.selected ?? false;

  const descendantIds = useMemo(() => getDescendantIds(node), [node]);
  const selectedDescendantCount = useMemo(
    () =>
      descendantIds.filter((did) =>
        isDeletionPage ? deleteCandidateIds?.has(did) : selectedMap.get(did)?.selected,
      ).length,
    [descendantIds, selectedMap, isDeletionPage, deleteCandidateIds]
  );

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDetail = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDetailExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCheckbox = (checked: boolean) => {
    setSelectedTargets((prev) =>
      prev.map((t) =>
        (t.test_target_id ?? "") === id ? { ...t, selected: checked } : t
      )
    );
  };

  const handleDeleteCheckbox = (checked: boolean) => {
    if (!setDeleteCandidateIds) return;
    const subtreeIds = getAllIdsInSubtree(node);
    setDeleteCandidateIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        subtreeIds.forEach((sid) => next.add(sid));
      } else {
        subtreeIds.forEach((sid) => next.delete(sid));
      }
      return next;
    });
  };

  const existing = findExistingTarget(id);
  const summary = get_target_change_summary(node.target, existing);
  const displayOperation = getDisplayOperation(node.target, summary);

  const indentPx = node.depth * 20;
  const isParentNode = hasChildren;
  const bgColor = node.depth === 0 ? "grey.50" : "transparent";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          pl: `${indentPx + 4}px`,
          pr: 1,
          py: 0.5,
          bgcolor: bgColor,
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: "36px",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {/* 展開アイコン */}
        {hasChildren ? (
          <IconButton size="small" onClick={toggleExpand} sx={{ p: 0.25 }}>
            {isExpanded ? (
              <KeyboardArrowDown fontSize="small" />
            ) : (
              <KeyboardArrowRight fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24 }} />
        )}

        {/* チェックボックススロット: isDeletionPage のときは削除選択チェック、それ以外は登録選択チェック */}
        {isDeletionPage ? (
          setDeleteCandidateIds ? (
            <Checkbox
              size="small"
              checked={deleteCandidateIds?.has(id) ?? false}
              onChange={(e) => handleDeleteCheckbox(e.target.checked)}
              inputProps={{ "aria-label": `削除: ${node.target.name ?? "(無名)"}` }}
              sx={{ p: 0.25, color: "error.main" }}
            />
          ) : (
            <Box sx={{ width: 24 }} />
          )
        ) : (
          <Checkbox
            size="small"
            checked={thisSel}
            onChange={(e) => handleCheckbox(e.target.checked)}
            inputProps={{ "aria-label": node.target.name ?? "(無名)" }}
            sx={{ p: 0.25 }}
          />
        )}

        {/* operation chip */}
        {displayOperation && (
          <Chip
            label={getOperationChipLabel(displayOperation)}
            size="small"
            color={getOperationChipColor(displayOperation)}
            sx={{ fontSize: "0.65rem", height: "18px" }}
          />
        )}

        {/* 非削除ページの DELETE ノード: operation chip の後に削除チェックボックスを追加表示 */}
        {!isDeletionPage && node.target.operation === "DELETE" && setDeleteCandidateIds && (
          <Checkbox
            size="small"
            checked={deleteCandidateIds?.has(id) ?? false}
            onChange={(e) => handleDeleteCheckbox(e.target.checked)}
            inputProps={{ "aria-label": `削除: ${node.target.name ?? "(無名)"}` }}
            sx={{ p: 0.25, color: "error.main" }}
          />
        )}

        {/* 名前 */}
        <Tooltip title={node.target.name ?? ""}>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: isParentNode ? "medium" : "normal",
            }}
          >
            {node.target.name ?? "(無名)"}
          </Typography>
        </Tooltip>

        {/* type chip */}
        {node.target.type && (
          <Chip
            label={(node.target.type ?? "").toLowerCase()}
            size="small"
            color={getTypeChipColor(node.target.type)}
            variant="outlined"
            sx={{ fontSize: "0.65rem", height: "18px" }}
          />
        )}

        {/* サブツリー選択カウント（情報表示のみ） */}
        {hasChildren && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
            {selectedDescendantCount}/{descendantIds.length}
          </Typography>
        )}

        {/* ケバブメニュー（親ノードのみ） */}
        {isParentNode && (
          <SubtreeMenu
            node={node}
            expandedNodes={expandedNodes}
            setExpandedNodes={setExpandedNodes}
            setSelectedTargets={setSelectedTargets}
            findExistingTarget={findExistingTarget}
            get_target_change_summary={get_target_change_summary}
            deleteCandidateIds={deleteCandidateIds}
            setDeleteCandidateIds={setDeleteCandidateIds}
            isDeletionPage={isDeletionPage}
          />
        )}

        {/* 詳細ボタン（全ノード） */}
        <Button
          size="small"
          endIcon={isDetailExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          onClick={toggleDetail}
          sx={{ fontSize: "0.7rem", py: 0, px: 0.5, minWidth: "52px" }}
        >
          詳細
        </Button>
      </Box>

      {/* 詳細パネル */}
      <Collapse in={isDetailExpanded}>
        <Box sx={{ pl: `${indentPx + 44}px`, pr: 1, pb: 0.5 }}>
          <DiffPanel target={node.target} existing={existing} summary={summary} />
        </Box>
      </Collapse>

      {/* 子ノード（再帰） */}
      {hasChildren && (
        <Collapse in={isExpanded}>
          {node.children.map((child, childIdx) => (
            <TreeNodeRow
              key={child.target.test_target_id ?? `child-${childIdx}`}
              node={child}
              expandedNodes={expandedNodes}
              detailExpandedIds={detailExpandedIds}
              selectedMap={selectedMap}
              setExpandedNodes={setExpandedNodes}
              setDetailExpandedIds={setDetailExpandedIds}
              setSelectedTargets={setSelectedTargets}
              findExistingTarget={findExistingTarget}
              get_target_change_summary={get_target_change_summary}
              deleteCandidateIds={deleteCandidateIds}
              setDeleteCandidateIds={setDeleteCandidateIds}
              isDeletionPage={isDeletionPage}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

// ──────────────────────── 一括選択メニュー ────────────────────────

interface BulkSelectMenuProps {
  pageTargetIds: Set<string>;
  setSelectedTargets: React.Dispatch<
    React.SetStateAction<OperationLogTestTarget[]>
  >;
  findExistingTarget: (id: string) => AppTestTarget | null;
  get_target_change_summary: SpecRegistrationHierarchicalViewProps["get_target_change_summary"];
}

function BulkSelectMenu({
  pageTargetIds,
  setSelectedTargets,
  findExistingTarget,
  get_target_change_summary,
}: BulkSelectMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const applyOp = (op: "CREATE" | "UPDATE") => {
    setSelectedTargets((prev) =>
      prev.map((t) =>
        pageTargetIds.has(t.test_target_id ?? "")
          ? (() => {
              const existing = findExistingTarget(t.test_target_id ?? "");
              const summary = get_target_change_summary(t, existing);
              const displayOperation = getDisplayOperation(t, summary);
              return { ...t, selected: displayOperation === op };
            })()
          : t
      )
    );
    handleClose();
  };

  const applyWithChanges = () => {
    setSelectedTargets((prev) =>
      prev.map((t) => {
        if (!pageTargetIds.has(t.test_target_id ?? "")) return t;
        const existing = findExistingTarget(t.test_target_id ?? "");
        const summary = get_target_change_summary(t, existing);
        return { ...t, selected: summary.has_changes };
      })
    );
    handleClose();
  };

  const invert = () => {
    setSelectedTargets((prev) =>
      prev.map((t) =>
        pageTargetIds.has(t.test_target_id ?? "")
          ? { ...t, selected: !t.selected }
          : t
      )
    );
    handleClose();
  };

  return (
    <>
      <Button
        size="small"
        endIcon={<ExpandMore fontSize="small" />}
        onClick={handleOpen}
        variant="outlined"
        sx={{ fontSize: "0.75rem" }}
      >
        一括選択
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => applyOp("CREATE")} dense>
          ✓ 新規 (CREATE) のみ選択
        </MenuItem>
        <MenuItem onClick={() => applyOp("UPDATE")} dense>
          ✎ 更新 (UPDATE) のみ選択
        </MenuItem>
        <MenuItem onClick={applyWithChanges} dense>
          ⚡ 変更ありのみ選択
        </MenuItem>
        <Divider />
        <MenuItem onClick={invert} dense>
          ⇄ 選択を反転
        </MenuItem>
      </Menu>
    </>
  );
}

// ──────────────────────── メインコンポーネント ────────────────────────

export function SpecRegistrationHierarchicalView({
  selectedPage,
  selectedPageTargets,
  selectedTargets,
  setSelectedTargets,
  findExistingTarget,
  get_target_change_summary,
  deleteCandidateIds,
  setDeleteCandidateIds,
  isDeletionPage = false,
}: SpecRegistrationHierarchicalViewProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [detailExpandedIds, setDetailExpandedIds] = useState<Set<string>>(
    new Set()
  );

  const treeRoots = useMemo(
    () => buildTree(selectedPageTargets),
    [selectedPageTargets]
  );

  const allNodeIds = useMemo(() => getAllNodeIds(treeRoots), [treeRoots]);
  const pageTargetIds = useMemo(
    () => new Set(selectedPageTargets.map((t) => t.test_target_id ?? "")),
    [selectedPageTargets]
  );

  // ツリー全行が参照する選択状態 Map。ここで 1 度だけ構築し各行へ渡す
  // (各行で構築すると 1 件のチェック切替で全行が O(N) 再構築 = 全体 O(N^2))
  const selectedMap = useMemo(
    () => new Map(selectedTargets.map((t) => [t.test_target_id ?? "", t])),
    [selectedTargets]
  );

  // Service ノード（depthが0かつtype=Serviceの最初の要素）
  const serviceNode = useMemo(
    () =>
      treeRoots.find(
        (n) => (n.target.type ?? "").toLowerCase() === "service"
      ),
    [treeRoots]
  );

  // Service 以外のルートノード（Service の配下）
  const contentRoots = useMemo(() => {
    if (!serviceNode) return treeRoots;
    return serviceNode.children;
  }, [treeRoots, serviceNode]);

  // Service サブツリー全体の ID を収集（Service ヘッダーの一括選択/削除チェックに使う）
  const serviceSubtreeIds = useMemo(
    () => (serviceNode ? getAllIdsInSubtree(serviceNode) : undefined),
    [serviceNode],
  );

  const pageSelectedCount = useMemo(
    () =>
      selectedTargets.filter(
        (t) => pageTargetIds.has(t.test_target_id ?? "") && t.selected
      ).length,
    [selectedTargets, pageTargetIds]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedTargets((prev) =>
        prev.map((t) =>
          pageTargetIds.has(t.test_target_id ?? "")
            ? { ...t, selected: checked }
            : t
        )
      );
    },
    [pageTargetIds, setSelectedTargets]
  );

  const handleExpandAll = useCallback(() => {
    setExpandedNodes(new Set(allNodeIds));
  }, [allNodeIds]);

  const handleCollapseAll = useCallback(() => {
    setExpandedNodes(new Set());
    setDetailExpandedIds(new Set());
  }, []);

  if (!selectedPage) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "text.secondary",
        }}
      >
        <Typography variant="h6">左側から画面を選択してください</Typography>
      </Box>
    );
  }

  const allPageSelected =
    pageTargetIds.size > 0 && pageSelectedCount === pageTargetIds.size;
  const somePageSelected =
    pageSelectedCount > 0 && pageSelectedCount < pageTargetIds.size;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* ツールバー */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: 1,
          borderColor: "divider",
          flexWrap: "wrap",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body2" fontWeight="medium">
          {isDeletionPage ? "削除対象" : "項目"}
        </Typography>

        {/* 選択操作（削除ページでは非表示） */}
        {!isDeletionPage && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Checkbox
              size="small"
              checked={allPageSelected}
              indeterminate={somePageSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              sx={{ p: 0.25 }}
            />
            <Button
              size="small"
              onClick={() => handleSelectAll(true)}
              sx={{ fontSize: "0.75rem", minWidth: "unset" }}
            >
              全選択
            </Button>
            <Button
              size="small"
              onClick={() => handleSelectAll(false)}
              sx={{ fontSize: "0.75rem", minWidth: "unset" }}
            >
              全解除
            </Button>
            <BulkSelectMenu
              pageTargetIds={pageTargetIds}
              setSelectedTargets={setSelectedTargets}
              findExistingTarget={findExistingTarget}
              get_target_change_summary={get_target_change_summary}
            />
          </Box>
        )}

        {!isDeletionPage && <Divider orientation="vertical" flexItem />}

        {/* 表示操作 */}
        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            size="small"
            startIcon={<UnfoldMore fontSize="small" />}
            onClick={handleExpandAll}
            sx={{ fontSize: "0.75rem" }}
          >
            全展開
          </Button>
          <Button
            size="small"
            startIcon={<UnfoldLess fontSize="small" />}
            onClick={handleCollapseAll}
            sx={{ fontSize: "0.75rem" }}
          >
            全折りたたみ
          </Button>
        </Box>

        <Box sx={{ flex: 1 }} />

        {!isDeletionPage && (
          <Typography variant="caption" color="text.secondary">
            {pageSelectedCount}/{pageTargetIds.size} 件選択
          </Typography>
        )}
      </Box>

      {/* Service ヘッダー */}
      {serviceNode && (
        <ServiceHeader
          serviceTarget={serviceNode.target}
          pageTargets={selectedPageTargets}
          findExistingTarget={findExistingTarget}
          get_target_change_summary={get_target_change_summary}
          selectedMap={selectedMap}
          setSelectedTargets={setSelectedTargets}
          deleteCandidateIds={deleteCandidateIds}
          setDeleteCandidateIds={setDeleteCandidateIds}
          serviceSubtreeIds={serviceSubtreeIds}
          isDeletionPage={isDeletionPage}
        />
      )}

      {/* ツリー本体 */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {contentRoots.length === 0 && (
          <Box sx={{ p: 2, color: "text.secondary" }}>
            <Typography variant="body2">表示するターゲットがありません</Typography>
          </Box>
        )}
        {contentRoots.map((node, rootIdx) => (
          <TreeNodeRow
            key={node.target.test_target_id ?? `root-${rootIdx}`}
            node={node}
            expandedNodes={expandedNodes}
            detailExpandedIds={detailExpandedIds}
            selectedMap={selectedMap}
            setExpandedNodes={setExpandedNodes}
            setDetailExpandedIds={setDetailExpandedIds}
            setSelectedTargets={setSelectedTargets}
            findExistingTarget={findExistingTarget}
            get_target_change_summary={get_target_change_summary}
            deleteCandidateIds={deleteCandidateIds}
            setDeleteCandidateIds={setDeleteCandidateIds}
            isDeletionPage={isDeletionPage}
          />
        ))}
      </Box>
    </Box>
  );
}
